<template>
  <div>
    <CardBase :headerBorder="false">
      <template #header>{{ $route.meta.title }}</template>
      <template #toolbar>
        <div class="flex mt-2 md:mt-0">
          <router-link
            v-if="userCan('create.transfer')"
            :to="{ name: 'TransferCreate' }"
            class="button button--primary poppins"
          >
            <font-awesome-icon :icon="['fas', 'plus-circle']" class="mr-1" />New
            Transfer
          </router-link>
        </div>
      </template>
      <template #content>
        <form
          class="form form-search"
          method="get"
          @submit.prevent="reloadTable"
        >
          <div class="form-search__row">
            <div class="form-search__double">
              <label class="form__label">Search</label>
              <input
                type="text"
                class="form__input"
                placeholder="Panel ID / Username"
                v-model="formData.search"
              />
            </div>
            <div class="form-search__double">
              <label class="form__label">Date Order</label>
              <select class="form__input" v-model="tableData.sort_dir">
                <option value="asc">Oldest First</option>
                <option value="desc">Newest First</option>
              </select>
            </div>
          </div>
          <div class="form-search__row">
            <div class="form-search__double">
              <label class="form__label">Start Date</label>
              <date-picker
                v-model="formData.start_date"
                type="date"
                :editable="false"
                :clearable="false"
                input-class="form__input"
                value-type="YYYY-MM-DD"
                format="DD MMMM YYYY"
                placeholder="Start Date"
                @close="checkEndDate"
                :disabled-date="notAfterToday"
              ></date-picker>
            </div>
            <div class="form-search__double">
              <label class="form__label">End Date</label>
              <date-picker
                v-model="formData.end_date"
                type="date"
                :editable="false"
                :clearable="false"
                input-class="form__input"
                value-type="YYYY-MM-DD"
                format="DD MMMM YYYY"
                placeholder="End Date"
                :disabled-date="notBeforeStart"
              ></date-picker>
            </div>
          </div>
          <div class="form-search__row">
            <div class="form-search__double">
              <label class="form__label">Asset</label>
              <multiselect
                v-model="formData.asset"
                :options="listAsset"
                :loading="listAssetLoading"
                :close-on-select="true"
                :hide-selected="false"
                track-by="id"
                label="name"
                :placeholder="
                  formData.asset ? formData.asset.name : 'All Asset'
                "
                :deselectLabel="'Remove selection'"
                :selectLabel="'Select this option'"
                @input="
                  formData.asset_id = formData.asset ? formData.asset.id : ''
                "
              >
                <template #noResult>No results found.</template>
              </multiselect>
            </div>
            <div class="form-search__double">
              <label class="form__label">Status</label>
              <select class="form__input" v-model="formData.status">
                <option value="">Show All</option>
                <option value="0">Pending</option>
                <option value="1">Approved</option>
                <option value="2">Rejected</option>
              </select>
            </div>
          </div>

          <div class="form-search__row">
            <div class="form-search__double" v-show="archivedNote">
              <label class="form__label">Archived {{ archivedNote }}</label>
              <select class="form__input" v-model="formData.archived">
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </div>
          </div>

          <div class="form-search__single">
            <FormNote class="mb-6"
              >Note: Start Date & End Date tidak akan berlaku jika melakukan
              search dengan status Pending.</FormNote
            >
          </div>

          <div class="form-search__action">
            <div class="w-6/12 flex">
              <BaseButton type="submit" buttonClass="secondary"
                >Search</BaseButton
              >
              <BaseButton
                type="button"
                class="mx-2"
                buttonClass="default"
                @click="reset"
                >Reset Search</BaseButton
              >
            </div>
          </div>
        </form>

        <vue-good-table
          :key="tableKey"
          mode="remote"
          theme="polar-bear"
          :rows="tableData.rows"
          :columns="tableData.columns"
          :total-rows="tableData.totalRows"
          :isLoading="tableData.isLoading"
          :sort-options="{
            enabled: false,
          }"
          :pagination-options="{
            enabled: true,
            perPageDropdown: perPageDropdown,
            dropdownAllowAll: false,
            setCurrentPage: tableData.currentPage,
            infoFn: tablePageInfo,
          }"
          styleClass="vgt-table striped bordered"
          @on-page-change="onPageChange"
          @on-per-page-change="onPerPageChange"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field == 'date'">
              {{ props.row.date }}
            </span>
            <span v-else-if="props.column.field == 'account.tooltip'">
              {{ props.row.account.tooltip }}
              <span class="block">Before: {{ props.row.before_credit }}</span>
              <span class="block">After: {{ props.row.after_credit }}</span>

              <FormNote
                class="mt-2"
                v-if="
                  props.row.status == 0 &&
                  props.row.account.balance.amount != props.row.before_credit
                "
                >Note: Balance account saat ini ({{
                  props.row.account.balance.amount
                }}) berbeda dengan balance saat form dibuat. Silahkan cek lebih
                lanjut.</FormNote
              >
            </span>
            <span v-else-if="props.column.field == 'transfer.account.tooltip'">
              {{ props.row.transfer.account.tooltip }}

              <span class="block"
                >Before: {{ props.row.transfer.before_credit }}</span
              >
              <span class="block"
                >After: {{ props.row.transfer.after_credit }}</span
              >

              <FormNote
                class="mt-2"
                v-if="
                  props.row.status == 0 &&
                  props.row.transfer.account.balance.amount !=
                    props.row.transfer.before_credit
                "
                >Note: Balance {{ props.row.transfer.account.tooltip }} saat ini
                ({{ props.row.transfer.account.balance.amount }}) berbeda dengan
                balance saat form dibuat. Silahkan cek lebih lanjut.</FormNote
              >
            </span>
            <span
              class="text-right block"
              v-else-if="props.column.field == 'amount'"
            >
              {{ props.row.amount }}
            </span>
            <span v-else-if="props.column.field == 'admin'">
              Created: {{ props.row.created_by + " / " + props.row.created_at }}
              <span
                class="block"
                v-if="props.row.processed_at && props.row.status != 0"
                >Processed:
                {{
                  props.row.processed_by + " / " + props.row.processed_at
                }}</span
              >
              <span
                  class="block mt-1"
                  v-if="props.row.rejected_at"
              >Rejected:
                {{
                  props.row.rejected_by + " / " + props.row.rejected_at
                }}</span
              >
            </span>
            <span v-else-if="props.column.field == 'status'">
              <span
                class="flex items-center text-yellow-500"
                v-if="props.row.status == 0"
              >
                <font-awesome-icon
                  :icon="['fas', 'question-circle']"
                  class="mr-2"
                  size="lg"
                />Pending
              </span>

              <span
                class="flex items-center text-green-500"
                v-if="props.row.status == 1"
              >
                <font-awesome-icon
                  :icon="['fas', 'check-circle']"
                  class="mr-2"
                  size="lg"
                />Approved
              </span>

              <span
                class="flex items-center text-red-500"
                v-else-if="props.row.status == 2"
              >
                <font-awesome-icon
                  :icon="['fas', 'exclamation-triangle']"
                  class="mr-2"
                  size="lg"
                />Rejected
              </span>
            </span>
            <span v-else-if="props.column.field == 'uuid'">
              <template
                v-if="
                  !props.row.account.member ||
                  !props.row.transfer.account.member
                "
              >
                <span class="flex text-left items-center text-red-500">
                  <font-awesome-icon
                    :icon="['fas', 'exclamation-triangle']"
                    class="mr-2"
                    size="lg"
                  />Missing Data
                </span>
              </template>
              <template v-else>
                <router-link
                  v-if="userCan('update.transfer')"
                  :to="{ name: 'TransferEdit', params: { id: props.row.id } }"
                  class="
                    w-full
                    button
                    border-gray-600
                    button
                    border border-solid
                    hover:bg-gray-600
                    hover:text-white
                    transition-all
                    duration-200
                    text-center text-gray-600
                  "
                  ><font-awesome-icon
                    class="mr-1"
                    :icon="
                      props.row.status != 2 ? ['fas', 'edit'] : ['far', 'eye']
                    "
                  />
                  <template v-if="props.row.status == 2">View</template
                  ><template v-else>Edit</template></router-link
                >
                <button
                  v-if="props.row.status == 0 && userCan('update.transfer')"
                  type="button"
                  class="
                    w-full
                    border-green-500
                    button
                    border border-solid
                    hover:bg-green-500
                    hover:text-white
                    transition-all
                    duration-200
                    my-2
                    text-center text-green-500
                  "
                  @click="processTransfer(props.row.id, 1)"
                >
                  <font-awesome-icon :icon="['fas', 'check-circle']" />
                  Approve
                </button>
                <button
                  v-if="props.row.status == 0 && userCan('delete.transfer')"
                  type="button"
                  class="
                    w-full
                    border-red-500
                    button
                    border border-solid
                    hover:bg-red-500
                    hover:text-white
                    transition-all
                    duration-200
                    text-center text-red-500
                  "
                  @click="processTransfer(props.row.id, 2)"
                >
                  <font-awesome-icon :icon="['fas', 'times-circle']" />
                  Reject
                </button>
              </template>
            </span>
          </template>
        </vue-good-table>
      </template>
    </CardBase>
  </div>
</template>

<script>
import { showListMixin } from "@/mixins/showListMixin";
import { datePickerMixin } from "@/mixins/datePickerMixin";
import { getListAssetMixin } from "@/mixins/getListAssetMixin";
import DatePicker from "vue2-datepicker";

export default {
  data() {
    return {
      archivedNote: '',
      listAsset: [],
      tableData: {
        columns: [
          {
            label: "Date",
            field: "date",
          },
          {
            label: "From",
            field: "account.tooltip",
          },
          {
            label: "To",
            field: "transfer.account.tooltip",
          },
          {
            label: "Amount",
            field: "amount",
          },
          {
            label: "Note",
            field: "note",
          },
          {
            label: "Status",
            field: "status",
          },
          {
            label: "Admin",
            field: "admin",
          },
          {
            label: "",
            field: "uuid",
            thClass: "vgt-table__action",
            tdClass: "vgt-table__action",
          },
        ],
        sort_by: "date",
        sort_dir: "desc",
      },
      formData: {
        archived: 0,
        status: 0,
        asset: null,
        asset_id: "",
        search: "",
        start_date: null,
        end_date: null,
      },
    };
  },
  mixins: [showListMixin, datePickerMixin, getListAssetMixin],
  components: {
    DatePicker,
  },
  created() {
    this.getListAsset();

    if (this.$route.query.search != undefined) {
      this.tableData.sort_dir = this.$route.query.sort_dir;
    }
  },
  watch: {
    $route() {
      const queryParams = this.$route.query;

      if (queryParams.asset_id != undefined) {
        this.listAsset.forEach((data) => {
          if (data.id == queryParams.asset_id) {
            this.formData.asset = data;
            this.formData.asset_id = data.id;
          }
        });
      }
    },
  },
  methods: {
    dateFormat(date) {
      return this.moment(date).format("DD MMM YYYY - HH:mm");
    },
    reset() {
      this.formData.search = "";
      this.formData.archived = 0;
      this.formData.start_date = this.moment(new Date()).format("YYYY-MM-DD");
      this.formData.end_date = this.moment(new Date()).format("YYYY-MM-DD");
      this.formData.status = "0";
      this.formData.asset = null;
      this.formData.asset_id = "";
      this.tableData.sort_dir = "desc";
      this.tableData.limit = 10;

      if (this.$route.query.search == undefined) {
        this.tableKey++;
      } else {
        this.tableData.currentPage = 1;
        this.$router.push({ name: "TransferList" }).catch(() => {});
      }
    },
    reloadTable() {
      if (this.formData.search && this.formData.search.length < 3) {
        this.$swal({
          position: "bottom-end",
          showConfirmButton: false,
          toast: true,
          timer: 3000,
          timerProgressBar: true,
          title: "Search query must have at least 3 characters",
          icon: "warning",
        });
      } else {
        this.tableData.currentPage = 1;
        this.$router
          .replace({
            query: {
              archived: this.formData.archived,
              search: this.formData.search,
              start_date: this.formData.start_date,
              end_date: this.formData.end_date,
              asset_id: this.formData.asset_id,
              status: this.formData.status,
              sort_dir: this.tableData.sort_dir,
              tz: new Date().getTime(),
            },
          })
          .catch(() => {});
      }
    },
    loadTableData() {
      this.tableData.isLoading = true;

      const queryParams = this.$route.query;
      let searchParam = "";

      if (queryParams.search != undefined) {
        this.formData = { ...this.formData, ...this.$route.query };
        this.tableData.sort_dir = this.$route.query.sort_dir;

        for (const key in queryParams) {
          if (key != "sort_dir") {
            searchParam += `&${key}=${queryParams[key]}`;
          }
        }
      } else {
        searchParam += `&start_date=${this.formData.start_date}&end_date=${this.formData.end_date}&status=0&archived=${this.formData.archived}`;
      }

      this.$http
        .get(
          process.env.VUE_APP_API_URL +
            `${this.$route.path}?page=${this.tableData.currentPage}&limit=${this.tableData.limit}&sort_by=${this.tableData.sort_by}&sort_dir=${this.tableData.sort_dir}${searchParam}`
        )
        .then((res) => {
          this.tableData.rows = res.data.data;
          this.tableData.totalRows = res.data.total;
          this.archivedNote = res.data.archived;
        })
        .finally(() => {
          this.tableData.isLoading = false;
        });
    },
    processTransfer(id, status) {
      this.$store.commit("toggleLoading");

      const url =
        process.env.VUE_APP_API_URL + "/transfer/" + id + "/process/" + status;

      this.$http({
        method: "patch",
        url: url,
      })
        .then((res) => {
          if (res.status == 200 || res.status == 201) {
            this.$swal({
              position: "bottom-end",
              showConfirmButton: false,
              toast: true,
              timer: 5000,
              timerProgressBar: true,
              title: res.data.message,
              icon: res.data.icon,
            });
          }
        })
        .catch((error) => {
          if (error.response) {
            this.formError = [];

            if (error.response.status == 409) {
              this.formError[0] = "<pre>" + error.response.data + "</pre>";
            } else {
              for (var err in error.response.data) {
                for (var msg in error.response.data[err]) {
                  this.formError.push(error.response.data[err][msg]);
                }
              }
            }
          }
        })
        .finally(() => {
          this.$store.commit("toggleLoading");

          this.loadTableData();
        });
    },
  },
};
</script>